import { ROTULO_STATUS_LEAD, type LeadDetalhado } from '@baiocchi/shared'
import {
  Abas,
  Alerta,
  AreaTexto,
  Botao,
  CabecalhoCartao,
  Campo,
  Cartao,
  Confirmacao,
  ContadorTexto,
  CorpoCartao,
  EstadoCarregando,
  Etiqueta,
  ListaDefinicao,
  Menu,
  Modal,
  Selecao,
  useAvisos,
} from '@baiocchi/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { chaves } from '../../api/chaves'
import { leadsApi } from '../../api/leads'
import { useSessao } from '../../autenticacao/contexto-sessao'
import { EstadoDeErro } from '../../componentes/estados-tela'
import {
  EtiquetaAbordagem,
  EtiquetaAtrasado,
  EtiquetaPercepcao,
  EtiquetaResponsavel,
  EtiquetaStatus,
  EtiquetaTemperatura,
} from '../../componentes/etiquetas-dominio'
import {
  IconeAgenda,
  IconeEditar,
  IconeTransferir,
  IconeVoltar,
  IconeWhatsapp,
} from '../../componentes/icones'
import { ROTULO_ORIGEM } from '../../dominio/rotulos'
import {
  opcoesDePessoas,
  opcoesDeReferencia,
  useAtendentesElegiveis,
  useEquipes,
} from '../../ganchos/usar-catalogos'
import { CabecalhoPagina } from '../../layout/layout-app'
import {
  formatarCep,
  formatarCpfCnpj,
  formatarDataHora,
  formatarDinheiro,
  formatarTelefone,
  formatarTempoRelativo,
} from '../../utilitarios/formatar'
import { HistoricoCliente } from './historico-cliente'
import { FormularioRegistrarInteracao } from './registrar-interacao'
import { TimelineLead } from './timeline-lead'

type AbaHistorico = 'interacoes' | 'cliente' | 'propostas'

/**
 * TELA-05 — Detalhes do lead / atendimento. **A tela mais importante do sistema.**
 *
 * Duas colunas: à esquerda a identidade do cliente e o formulário de interação; à
 * direita o histórico em abas. No celular as colunas empilham, com o formulário
 * primeiro — é o que o corretor abre para usar.
 */
export function PaginaDetalheLead() {
  const { uuid = '' } = useParams()
  const navegar = useNavigate()
  const { pode, peloMenos } = useSessao()
  const avisos = useAvisos()
  const clienteConsulta = useQueryClient()

  const [aba, setAba] = useState<AbaHistorico>('interacoes')
  const [modalTransferir, setModalTransferir] = useState(false)
  const [modalReabrir, setModalReabrir] = useState(false)
  const [confirmarAssumir, setConfirmarAssumir] = useState(false)

  const consulta = useQuery({
    queryKey: chaves.leads.detalhe(uuid),
    queryFn: ({ signal }) => leadsApi.detalhar(uuid, signal),
    enabled: uuid !== '',
  })

  const invalidarTudo = () => {
    void clienteConsulta.invalidateQueries({ queryKey: chaves.leads.todos })
    void clienteConsulta.invalidateQueries({ queryKey: chaves.painel.todos })
    void clienteConsulta.invalidateQueries({ queryKey: chaves.agendamentos.todos })
  }

  const assumir = useMutation({
    mutationFn: () => leadsApi.assumir(uuid),
    onSuccess: () => {
      avisos.sucesso('Lead assumido', 'Você é o responsável a partir de agora.')
      setConfirmarAssumir(false)
      invalidarTudo()
    },
    onError: (erro) => avisos.erro('Não foi possível assumir', erro.message),
  })

  /**
   * O registro da abertura de conversa é do **servidor** (`WHATSAPP_ABERTO`), e a
   * tela só abre o link depois da resposta: abrir antes deixaria o registro
   * dependente de o navegador não ter bloqueado a aba.
   */
  const whatsapp = useMutation({
    mutationFn: () => leadsApi.abrirWhatsapp(uuid),
    onSuccess: (resultado) => {
      window.open(resultado.link, '_blank', 'noopener,noreferrer')
      void clienteConsulta.invalidateQueries({ queryKey: chaves.leads.timeline(uuid) })
      void clienteConsulta.invalidateQueries({ queryKey: chaves.leads.detalhe(uuid) })
    },
    onError: (erro) => avisos.erro('Não foi possível abrir a conversa', erro.message),
  })

  if (consulta.isPending) return <EstadoCarregando descricao="Carregando atendimento…" />
  if (consulta.isError) {
    return (
      <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
    )
  }

  const lead = consulta.data
  const semResponsavel = lead.responsavel === null
  const terminal =
    lead.status === 'VENDA_REALIZADA' ||
    lead.status === 'VENDA_NAO_REALIZADA' ||
    lead.status === 'FINALIZADO'

  return (
    <>
      <CabecalhoPagina
        titulo={lead.cliente.nome}
        descricao={`Atendimento aberto em ${formatarDataHora(lead.chegadaEm)} · origem ${
          ROTULO_ORIGEM[lead.origemTipo]
        }`}
        acoes={
          <>
            <Botao
              variante="contorno"
              onClick={() => navegar(-1)}
              iconeEsquerda={<IconeVoltar className="h-4 w-4" />}
            >
              Voltar
            </Botao>
            {pode('CLIENTE_EDITAR') && (
              <Botao
                variante="contorno"
                onClick={() => navegar(`/leads/${lead.uuid}/editar`)}
                iconeEsquerda={<IconeEditar className="h-4 w-4" />}
              >
                Editar cliente
              </Botao>
            )}
            <Menu
              gatilho={<Botao variante="contorno">Mais ações</Botao>}
              itens={[
                {
                  rotulo: 'Transferir lead',
                  icone: <IconeTransferir className="h-4 w-4" />,
                  desabilitado: !pode('LEAD_TRANSFERIR') || terminal,
                  motivoBloqueio: terminal
                    ? 'Lead em situação terminal não é transferível.'
                    : 'Seu perfil não permite transferir leads.',
                  onSelecionar: () => setModalTransferir(true),
                },
                {
                  rotulo: 'Assumir lead',
                  desabilitado: !pode('LEAD_ASSUMIR') || !semResponsavel || terminal,
                  motivoBloqueio: semResponsavel
                    ? 'Lead em situação terminal.'
                    : 'Este lead já tem responsável — trocar dono é transferência.',
                  onSelecionar: () => setConfirmarAssumir(true),
                },
                {
                  rotulo: 'Reabrir lead',
                  desabilitado: !pode('LEAD_REABRIR') || !terminal,
                  motivoBloqueio: !terminal
                    ? 'Só lead encerrado pode ser reaberto.'
                    : 'Reabrir é privilégio de gestão.',
                  onSelecionar: () => setModalReabrir(true),
                },
                {
                  rotulo: 'Gerar proposta',
                  desabilitado: true,
                  motivoBloqueio: 'Módulo de propostas previsto para a Fase 2.',
                  onSelecionar: () => undefined,
                },
              ]}
            />
          </>
        }
      />

      {semResponsavel && (
        <Alerta
          tom="atencao"
          titulo="Este lead está sem responsável"
          className="mb-4"
          acoes={
            pode('LEAD_ASSUMIR') && !terminal ? (
              <Botao tamanho="sm" onClick={() => setConfirmarAssumir(true)}>
                Assumir atendimento
              </Botao>
            ) : undefined
          }
        >
          Enquanto ninguém for o responsável, não há quem responda pelo atendimento. Assuma o
          lead ou atribua um responsável antes de registrar interação.
        </Alerta>
      )}

      {lead.atrasado && (
        <Alerta tom="perigo" titulo="Retorno vencido" className="mb-4">
          O prazo de retorno deste lead venceu
          {lead.proximoRetornoEm ? ` ${formatarTempoRelativo(lead.proximoRetornoEm)}` : ''}. Ele
          aparece na fila de atrasados até que uma interação ou um novo agendamento seja registrado.
        </Alerta>
      )}

      {terminal && (
        <Alerta tom="info" className="mb-4" titulo={`Atendimento encerrado: ${ROTULO_STATUS_LEAD[lead.status]}`}>
          {lead.finalizadoAutomaticamente
            ? 'Encerrado automaticamente por inatividade. '
            : ''}
          Leads encerrados não aceitam nova interação nem transferência. Reabrir é privilégio de
          gestão e exige justificativa.
        </Alerta>
      )}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        {/* --- Coluna esquerda --- */}
        <div className="flex flex-col gap-5">
          <Cartao semPadding>
            <CabecalhoCartao titulo="Dados pessoais" />
            <CorpoCartao className="flex flex-col gap-4">
              <ListaDefinicao
                itens={[
                  { rotulo: 'Nome', valor: lead.cliente.nome },
                  { rotulo: 'E-mail', valor: lead.cliente.email ?? '—' },
                  {
                    rotulo: 'Contato principal',
                    valor: (
                      <a
                        href={`tel:${lead.cliente.contatoPrincipal}`}
                        className="tabular-nums text-marca-600 hover:underline"
                      >
                        {formatarTelefone(lead.cliente.contatoPrincipal)}
                      </a>
                    ),
                  },
                  ...(lead.cliente.contatoAlternativo
                    ? [
                        {
                          rotulo: 'Contato alternativo',
                          valor: formatarTelefone(lead.cliente.contatoAlternativo),
                        },
                      ]
                    : []),
                  ...(lead.cliente.documento
                    ? [
                        {
                          rotulo: lead.cliente.tipoDocumento ?? 'Documento',
                          valor: formatarCpfCnpj(lead.cliente.documento),
                        },
                      ]
                    : []),
                ]}
              />

              <div className="border-t border-borda pt-4">
                <ListaDefinicao
                  colunas={2}
                  itens={[
                    { rotulo: 'Unidade', valor: lead.unidade.titulo },
                    { rotulo: 'Equipe', valor: lead.equipe?.titulo ?? '—' },
                    {
                      rotulo: 'Responsável',
                      valor: <EtiquetaResponsavel responsavel={lead.responsavel} />,
                    },
                    // C3 — só aparece quando é outra pessoa. Repetir o mesmo nome em
                    // duas linhas seguidas não informa nada; a linha existe justamente
                    // para mostrar que o lead passou de mão, do pré-atendimento para o
                    // corretor de plantão.
                    ...(lead.responsavelInicial !== null &&
                    lead.responsavelInicial.uuid !== lead.responsavel?.uuid
                      ? [
                          {
                            rotulo: 'Recebido inicialmente por',
                            valor: lead.responsavelInicial.nome,
                          },
                        ]
                      : []),
                    {
                      rotulo: 'Ponto de conversão',
                      valor: lead.pontoConversao?.titulo ?? '—',
                    },
                    { rotulo: 'Produto', valor: lead.produtoAssociado?.titulo ?? '—' },
                    {
                      rotulo: 'Cadastrado por',
                      valor: lead.criadoPor?.nome ?? 'integração',
                    },
                  ]}
                />
              </div>

              <div className="flex flex-wrap gap-1.5 border-t border-borda pt-4">
                <EtiquetaStatus status={lead.status} />
                <EtiquetaTemperatura temperatura={lead.temperatura} />
                <EtiquetaPercepcao percepcao={lead.percepcao} />
                <EtiquetaAbordagem tentativas={lead.tentativasContato} />
                <EtiquetaAtrasado atrasado={lead.atrasado} />
                {lead.tags.map((tag) => (
                  <Etiqueta key={tag.id} tom="marca">
                    {tag.titulo}
                  </Etiqueta>
                ))}
              </div>

              {lead.vendaRealizadaEm ? (
                <div className="border-t border-borda pt-4">
                  <ListaDefinicao
                    colunas={2}
                    itens={[
                      { rotulo: 'Venda em', valor: formatarDataHora(lead.vendaRealizadaEm) },
                      { rotulo: 'Valor', valor: formatarDinheiro(lead.valorNegocio) },
                    ]}
                  />
                </div>
              ) : null}

              {lead.motivoVendaNaoRealizada ? (
                <div className="border-t border-borda pt-4">
                  <ListaDefinicao
                    itens={[
                      {
                        rotulo: 'Motivo da venda não realizada',
                        valor: lead.motivoVendaNaoRealizada.titulo,
                      },
                    ]}
                  />
                </div>
              ) : null}

              {(lead.cliente.endereco.logradouro || lead.cliente.endereco.cidade) && (
                <div className="border-t border-borda pt-4">
                  <ListaDefinicao
                    itens={[
                      {
                        rotulo: 'Endereço',
                        valor: [
                          lead.cliente.endereco.logradouro,
                          lead.cliente.endereco.complemento,
                          lead.cliente.endereco.bairro,
                          lead.cliente.endereco.cidade && lead.cliente.endereco.estado
                            ? `${lead.cliente.endereco.cidade}/${lead.cliente.endereco.estado}`
                            : lead.cliente.endereco.cidade,
                          lead.cliente.endereco.cep
                            ? `CEP ${formatarCep(lead.cliente.endereco.cep)}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · '),
                      },
                    ]}
                  />
                </div>
              )}
            </CorpoCartao>
          </Cartao>

          {lead.linkWhatsapp ? (
            <Botao
              variante="whatsapp"
              tamanho="lg"
              larguraTotal
              carregando={whatsapp.isPending}
              onClick={() => whatsapp.mutate()}
              iconeEsquerda={<IconeWhatsapp />}
            >
              {formatarTelefone(lead.cliente.whatsapp ?? lead.cliente.contatoPrincipal)}
            </Botao>
          ) : null}

          {lead.proximoAgendamento ? (
            <Cartao className="border-info/30 bg-info-suave">
              <div className="flex items-start gap-3">
                <IconeAgenda className="mt-0.5 h-5 w-5 shrink-0 text-info" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-conteudo">Próximo compromisso</p>
                  <p className="mt-0.5 text-sm text-conteudo-suave">
                    {formatarDataHora(lead.proximoAgendamento.iniciaEm)} ·{' '}
                    {lead.proximoAgendamento.motivo.titulo}
                  </p>
                  <p className="text-xs text-conteudo-fraco">
                    com {lead.proximoAgendamento.responsavel.nome}
                  </p>
                  <Link
                    to="/agendamentos"
                    className="mt-1.5 inline-block text-xs font-medium text-marca-600 hover:underline"
                  >
                    Ver na agenda
                  </Link>
                </div>
              </div>
            </Cartao>
          ) : null}

          {/**
           * Lead sem responsável: gestor e administrador escrevem direto (atendem
           * por exceção e não têm carteira própria); o supervisor assume antes,
           * porque o registro precisa ficar vinculado a um responsável. O atendente
           * não chega até aqui — o escopo dele não alcança lead sem responsável e a
           * API responde `404` já na leitura.
           */}
          {!terminal && (!semResponsavel || peloMenos('GESTOR')) && pode('INTERACAO_CRIAR') && (
            <FormularioRegistrarInteracao lead={lead} />
          )}

          {!terminal && semResponsavel && !peloMenos('GESTOR') && (
            <Cartao>
              <p className="text-sm text-conteudo-suave">
                Para registrar atendimento neste lead, assuma o atendimento primeiro — o registro
                precisa ficar vinculado a um responsável.
              </p>
            </Cartao>
          )}
        </div>

        {/* --- Coluna direita: histórico --- */}
        <Cartao semPadding className="flex min-h-96 flex-col">
          <Abas
            ativa={aba}
            onAlterar={setAba}
            itens={[
              { valor: 'interacoes', rotulo: 'Interação do lead' },
              { valor: 'cliente', rotulo: 'Histórico do cliente' },
              { valor: 'propostas', rotulo: 'Propostas' },
            ]}
          />

          <div className="min-h-0 flex-1">
            {aba === 'interacoes' && <TimelineLead leadUuid={lead.uuid} />}
            {aba === 'cliente' && (
              <HistoricoCliente clienteUuid={lead.cliente.uuid} leadAtualUuid={lead.uuid} />
            )}
            {aba === 'propostas' && (
              <div className="p-4">
                <Alerta tom="info" titulo="Propostas entram na Fase 2">
                  O módulo de propostas (TELA-07) depende do editor de texto rico e da sanitização
                  no servidor. A transição para a situação <strong>Proposta</strong> já existe e
                  exige uma proposta vinculada, então ela só passa com dado importado.
                </Alerta>
              </div>
            )}
          </div>
        </Cartao>
      </div>

      <ModalTransferirLead
        aberto={modalTransferir}
        lead={lead}
        onFechar={() => setModalTransferir(false)}
        onConcluido={() => {
          setModalTransferir(false)
          invalidarTudo()
          avisos.sucesso('Lead transferido')
        }}
      />

      <ModalReabrirLead
        aberto={modalReabrir}
        leadUuid={lead.uuid}
        onFechar={() => setModalReabrir(false)}
        onConcluido={() => {
          setModalReabrir(false)
          invalidarTudo()
          avisos.sucesso('Lead reaberto', 'A reabertura entrou na timeline e na auditoria.')
        }}
      />

      <Confirmacao
        aberto={confirmarAssumir}
        onFechar={() => setConfirmarAssumir(false)}
        onConfirmar={() => assumir.mutate()}
        processando={assumir.isPending}
        titulo="Assumir este lead?"
        rotuloConfirmar="Assumir"
        descricao={
          <>
            Você passa a ser o responsável por <strong>{lead.cliente.nome}</strong> e o lead sai da
            fila &quot;sem responsável&quot; da equipe. A ação entra na timeline.
          </>
        }
      />
    </>
  )
}

function ModalTransferirLead({
  aberto,
  lead,
  onFechar,
  onConcluido,
}: {
  aberto: boolean
  lead: LeadDetalhado
  onFechar: () => void
  onConcluido: () => void
}) {
  const [equipeId, setEquipeId] = useState('')
  const [responsavelUuid, setResponsavelUuid] = useState('')
  const [motivo, setMotivo] = useState('')

  const equipes = useEquipes()
  const equipeAlvo = equipeId ? Number(equipeId) : (lead.equipe?.id ?? null)
  const atendentes = useAtendentesElegiveis(equipeAlvo)

  const transferir = useMutation({
    mutationFn: () =>
      leadsApi.transferir(lead.uuid, {
        motivo: motivo.trim(),
        ...(equipeId ? { equipeId: Number(equipeId) } : {}),
        responsavelUuid: responsavelUuid || null,
      }),
    onSuccess: () => {
      setEquipeId('')
      setResponsavelUuid('')
      setMotivo('')
      onConcluido()
    },
  })

  const podeEnviar = motivo.trim().length >= 10 && (equipeId !== '' || responsavelUuid !== '')

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      travado={transferir.isPending}
      titulo="Transferir lead"
      descricao="Gera uma interação de transferência na timeline, com responsável e equipe anteriores."
      rodape={
        <>
          <Botao variante="contorno" onClick={onFechar} disabled={transferir.isPending}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => transferir.mutate()}
            carregando={transferir.isPending}
            disabled={!podeEnviar}
          >
            Transferir
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {transferir.isError ? (
          <Alerta tom="perigo">{transferir.error.message}</Alerta>
        ) : null}

        <Campo rotulo="Nova equipe" dica="Vazio mantém a equipe atual. Mudar a equipe move a unidade.">
          {(atributos) => (
            <Selecao
              {...atributos}
              vazio={`Manter ${lead.equipe?.titulo ?? 'sem equipe'}`}
              value={equipeId}
              opcoes={opcoesDeReferencia(equipes.data?.dados)}
              onChange={(evento) => {
                setEquipeId(evento.target.value)
                setResponsavelUuid('')
              }}
            />
          )}
        </Campo>

        <Campo
          rotulo="Novo responsável"
          dica="Somente atendentes ativos e membros da equipe de destino."
        >
          {(atributos) => (
            <Selecao
              {...atributos}
              vazio="Devolver ao pool (sem responsável)"
              value={responsavelUuid}
              opcoes={opcoesDePessoas(atendentes.data?.dados)}
              onChange={(evento) => setResponsavelUuid(evento.target.value)}
            />
          )}
        </Campo>

        <Campo
          rotulo="Motivo da transferência"
          obrigatorio
          acessorio={<ContadorTexto valor={motivo} minimo={10} maximo={1000} />}
          erro={
            motivo.length > 0 && motivo.trim().length < 10
              ? 'Descreva o motivo com ao menos 10 caracteres.'
              : null
          }
        >
          {(atributos) => (
            <AreaTexto
              {...atributos}
              rows={3}
              value={motivo}
              temErro={atributos.temErro}
              placeholder="Ex.: Cliente mudou o interesse para locação."
              onChange={(evento) => setMotivo(evento.target.value)}
            />
          )}
        </Campo>
      </div>
    </Modal>
  )
}

function ModalReabrirLead({
  aberto,
  leadUuid,
  onFechar,
  onConcluido,
}: {
  aberto: boolean
  leadUuid: string
  onFechar: () => void
  onConcluido: () => void
}) {
  const [justificativa, setJustificativa] = useState('')

  const reabrir = useMutation({
    mutationFn: () => leadsApi.reabrir(leadUuid, justificativa.trim()),
    onSuccess: () => {
      setJustificativa('')
      onConcluido()
    },
  })

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      travado={reabrir.isPending}
      titulo="Reabrir lead encerrado"
      descricao="É a única operação de lead que entra na timeline e também na auditoria — ela desfaz um número já publicado."
      rodape={
        <>
          <Botao variante="contorno" onClick={onFechar} disabled={reabrir.isPending}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => reabrir.mutate()}
            carregando={reabrir.isPending}
            disabled={justificativa.trim().length < 20}
          >
            Reabrir
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {reabrir.isError ? <Alerta tom="perigo">{reabrir.error.message}</Alerta> : null}

        <Alerta tom="atencao">
          Reabrir limpa a data de encerramento, a venda registrada e o motivo de perda. Relatórios
          do período já fechado passam a contar este lead como ativo.
        </Alerta>

        <Campo
          rotulo="Justificativa"
          obrigatorio
          acessorio={<ContadorTexto valor={justificativa} minimo={20} />}
        >
          {(atributos) => (
            <AreaTexto
              {...atributos}
              rows={4}
              value={justificativa}
              temErro={atributos.temErro}
              placeholder="Explique por que este atendimento precisa voltar a ficar ativo."
              onChange={(evento) => setJustificativa(evento.target.value)}
            />
          )}
        </Campo>
      </div>
    </Modal>
  )
}
